import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { debugZodResolver } from '@/lib/debugZodResolver';
import TourConfigBreadcrumbs from '@/features/dashboard/tourConfig/components/TourConfigBreadcrumbs.tsx';
import TourConfigHeader from '@/features/dashboard/tourConfig/components/TourConfigHeader.tsx';
import TourInsuranceConfigCard from '@/features/dashboard/tourInsurances/components/TourInsuranceConfigCard.tsx';
import {
  tourInsuranceSchema,
  TourInsuranceFormData,
  tourInsuranceDefaultValues,
} from '@/features/dashboard/tourInsurances/schemas/tourInsuranceSchema.ts';
import { tourInsurancesConfigRoute } from '@/features/dashboard/tourInsurances/routes/tourInsurancesConfigRoute.ts';
import useGetTourInsurances from '@/features/dashboard/tourInsurances/hooks/useGetTourInsurances.ts';
import usePutTourInsurances from '@/features/dashboard/tourInsurances/hooks/usePutTourInsurances.ts';
import { tourInsurancesMapper } from '@/features/dashboard/tourInsurances/types/TourInsurancesDto.mapper.ts';
import useGetTour from '@/features/dashboard/tours/hooks/useGetTour.ts';
import { TourStatus } from '@/features/dashboard/tours/types/TourStatus.ts';

const TourInsurancesConfigPage = () => {
  const navigate = useNavigate();
  const { tourId } = tourInsurancesConfigRoute.useParams();

  const insuranceForm = useForm<TourInsuranceFormData>({
    resolver: debugZodResolver(tourInsuranceSchema),
    mode: 'onChange',
    defaultValues: tourInsuranceDefaultValues,
  });

  const { mutate, isPending } = usePutTourInsurances(tourId);
  const { data: tour } = useGetTour(tourId);
  const { data: tourInsurances, isPending: tourInsurancesLoading } =
    useGetTourInsurances(tourId);

  useEffect(() => {
    if (tourInsurancesLoading) return;
    if (tourInsurances) {
      insuranceForm.reset(tourInsurancesMapper.fromDtoToForm(tourInsurances));
    }
  }, [tourInsurances, tourInsurancesLoading, insuranceForm]);

  const onSubmit = (values: TourInsuranceFormData) => {
    mutate(values, {
      onSuccess: () => {
        if (tour?.status === TourStatus.DRAFT) {
          navigate({
            to: `/app/tours/${tourId}`,
            search: { justCreated: true },
          });
          return;
        }
        navigate({ to: `/app/tours/${tourId}` });
      },
    });
  };

  return (
    <div className="space-y-4">
      <TourConfigBreadcrumbs />
      <FormProvider {...insuranceForm}>
        <form
          className="flex-1 space-y-4"
          onSubmit={insuranceForm.handleSubmit(onSubmit)}
        >
          <TourConfigHeader
            title="Ubezpieczenie"
            isLoading={isPending}
            submit={insuranceForm.handleSubmit(onSubmit)}
            submitText="Zapisz"
          />
          <div className="mx-auto max-w-[600px] space-y-2">
            <TourInsuranceConfigCard />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default TourInsurancesConfigPage;
